import { parseISO } from 'date-fns'
import { formatInTimeZone, toZonedTime } from 'date-fns-tz'

/**
 * Date utility functions for UTC storage and local timezone display
 */

const DEFAULT_TIMEZONE = 'Asia/Ho_Chi_Minh'

/**
 * Convert UTC date string to local timezone date object
 */
export function utcToLocal(dateString: string, timezone: string = DEFAULT_TIMEZONE): Date {
  return toZonedTime(parseISO(dateString), timezone)
}

/**
 * Format UTC date string for display in local timezone
 */
export function formatLocalDate(
  dateString: string | undefined | null,
  formatString: string = 'dd/MM/yyyy',
  timezone: string = DEFAULT_TIMEZONE
): string {
  if (!dateString) {
    return 'N/A'
  }
  try {
    const date = parseISO(dateString)
    return formatInTimeZone(date, timezone, formatString)
  } catch (error) {
    console.error('Error formatting date:', dateString, error)
    return 'N/A'
  }
}

/**
 * Format UTC date string for display with time
 */
export function formatLocalDateTime(
  dateString: string,
  formatString: string = 'dd/MM/yyyy HH:mm',
  timezone: string = DEFAULT_TIMEZONE
): string {
  const date = parseISO(dateString)
  return formatInTimeZone(date, timezone, formatString)
}

/**
 * Convert local date to UTC ISO string for storage
 */
export function localToUtc(date: Date): string {
  return date.toISOString()
}

/**
 * Get current date in UTC ISO format
 */
export function getCurrentUtcDate(): string {
  return new Date().toISOString()
}

/**
 * Parse date string (handles various formats) and convert to UTC
 */
export function parseToUtc(dateString: string): string {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date format: ${dateString}`)
  }
  return date.toISOString()
}

/**
 * Format date for date input (YYYY-MM-DD)
 */
export function formatForDateInput(dateString: string | undefined | null, timezone: string = DEFAULT_TIMEZONE): string {
  if (!dateString) {
    return getTodayDateInput(timezone)
  }
  try {
    const date = parseISO(dateString)
    if (isNaN(date.getTime())) {
      return getTodayDateInput(timezone)
    }
    return formatInTimeZone(date, timezone, 'yyyy-MM-dd')
  } catch (error) {
    console.error('Error formatting date for input:', dateString, error)
    return getTodayDateInput(timezone)
  }
}

/**
 * Get today's date formatted for date input (YYYY-MM-DD)
 */
export function getTodayDateInput(timezone: string = DEFAULT_TIMEZONE): string {
  const today = new Date()
  return formatInTimeZone(today, timezone, 'yyyy-MM-dd')
}

/**
 * Get first day of current month formatted for date input (YYYY-MM-DD)
 */
export function getFirstDayOfMonth(timezone: string = DEFAULT_TIMEZONE): string {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  return formatInTimeZone(firstDay, timezone, 'yyyy-MM-dd')
}

/**
 * Get last day of current month formatted for date input (YYYY-MM-DD)
 */
export function getLastDayOfMonth(timezone: string = DEFAULT_TIMEZONE): string {
  const today = new Date()
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  return formatInTimeZone(lastDay, timezone, 'yyyy-MM-dd')
}

